import { PlusOutlined } from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useTable } from '@refinedev/antd'
import { type HttpError, useInvalidate } from '@refinedev/core'
import {
  Breadcrumb,
  Button,
  Form,
  Input,
  message,
  Select,
  Space,
  Table,
} from 'antd'
import { useState } from 'react'

import client from '@/api/client'
import { unwrapApiData } from '@/api/unwrapApiResult'
import { useTableRowNavigation } from '@/hooks/useTableRowNavigation'
import { getPersistedPageSize, tablePaginationConfig } from '@/types/api'

import { promoCodeColumns } from './columns'
import { CreatePromoCodeModal } from './components/CreatePromoCodeModal'

type PromoCodeDto = components['schemas']['PromoCodeDto']

interface PromoCodeFilters {
  code?: string
  isActive?: boolean
}

export default function PromoCodesListPage() {
  const [createOpen, setCreateOpen] = useState(false)
  const onRow = useTableRowNavigation('promo-codes')
  const invalidate = useInvalidate()

  const { tableProps, searchFormProps } = useTable<
    PromoCodeDto,
    HttpError,
    PromoCodeFilters
  >({
    resource: 'promo-codes',
    pagination: { pageSize: getPersistedPageSize() },
    syncWithLocation: true,
    onSearch: (values) => [
      { field: 'code', operator: 'eq', value: values.code || undefined },
      {
        field: 'isActive',
        operator: 'eq',
        value: values.isActive ?? undefined,
      },
    ],
  })

  const handleToggleActive = async (record: PromoCodeDto, checked: boolean) => {
    try {
      const result = await client.PATCH(
        '/v1/admin/promo_codes/{promo_code_id}',
        {
          params: { path: { promo_code_id: record.id } },
          body: { isActive: checked },
        },
      )
      unwrapApiData(result, 'Failed to update promo code')
      void message.success(
        `Promo code ${checked ? 'activated' : 'deactivated'}`,
      )
      void invalidate({ resource: 'promo-codes', invalidates: ['list'] })
    } catch (error) {
      console.error(error)
      if (error instanceof Error) void message.error(error.message)
    }
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[{ title: 'Promo Codes' }]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
        >
          Create Promo Code
        </Button>
      </div>

      <Form {...searchFormProps} layout="inline" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Form.Item name="code" noStyle>
            <Input.Search
              placeholder="Search by code..."
              style={{ width: 220 }}
              onSearch={() => searchFormProps.form?.submit()}
              allowClear
            />
          </Form.Item>
          <Form.Item name="isActive" noStyle>
            <Select
              placeholder="Status"
              style={{ width: 140 }}
              allowClear
              onChange={() => searchFormProps.form?.submit()}
              options={[
                { label: 'Active', value: true },
                { label: 'Inactive', value: false },
              ]}
            />
          </Form.Item>
        </Space>
      </Form>

      <Table
        {...tableProps}
        pagination={tablePaginationConfig(tableProps)}
        rowKey="id"
        onRow={onRow}
        columns={promoCodeColumns(handleToggleActive)}
        scroll={{ x: 900 }}
      />

      <CreatePromoCodeModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  )
}
